import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { Check, TrendingUp, Shield, Globe, Users } from "lucide-react"
import * as LucideIcons from "lucide-react"
import Layout from "@/components/Layout"
import PhoneMockup from "@/components/PhoneMockup"
import AssistantCard from "@/components/AssistantCard"
import SectionHeader from "@/components/SectionHeader"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { ROUTE_PATHS, ASSISTANTS_LIST, INDUSTRIES_LIST, PROOF_BAR_ITEMS } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion"
import { IMAGES } from "@/assets/images"

const BUSINESS_VALUE = [
  {
    icon: TrendingUp,
    title: "Increase Customer Value",
    desc: "Give customers a reason to stay engaged every single day with practical household support they genuinely use.",
  },
  {
    icon: Shield,
    title: "Improve Engagement & Retention",
    desc: "Reduce churn and deepen loyalty by embedding useful daily-life support into your product or benefit offering.",
  },
  {
    icon: Globe,
    title: "Create Social Impact at Scale",
    desc: "Deliver measurable ESG and CSI impact by helping families navigate financial, educational, and health challenges.",
  },
]

const FAMILY_MEMBERS = [
  { label: "Parent", color: "bg-primary" },
  { label: "Teen", color: "bg-accent" },
  { label: "Child", color: "bg-primary/70" },
  { label: "Grandparent", color: "bg-accent/70" },
  { label: "Partner", color: "bg-primary/50" },
]

export default function Home() {
  const featuredAssistants = ASSISTANTS_LIST.slice(0, 9)

  return (
    <Layout>
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-primary/5" />
        <div className="container mx-auto px-4 py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="space-y-8"
            >
              <motion.div variants={staggerItem}>
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-4">
                  Built for Africa
                </span>
                <h1 className="text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                  Grow customer value through everyday family support
                </h1>
              </motion.div>
              <motion.p
                variants={staggerItem}
                className="text-xl text-muted-foreground max-w-xl leading-relaxed"
              >
                Help customers, members and employees with practical support across money, education, health and daily life — through messaging platforms they already use.
              </motion.p>
              <motion.div
                variants={staggerItem}
                className="flex flex-wrap gap-4"
              >
                <Button asChild size="lg" className="text-base px-8">
                  <Link to={ROUTE_PATHS.FOR_BUSINESS}>See Sammo for Your Business</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-base px-8">
                  <Link to={ROUTE_PATHS.CONTACT}>Book a Demo</Link>
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="flex justify-center"
            >
              <PhoneMockup />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-8 border-y border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {PROOF_BAR_ITEMS.map((item, i) => {
              const LucideMap = LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>
              const IconComponent = LucideMap[item.icon]
              if (!IconComponent) return null
              return (
                <div key={i} className="flex items-center gap-2">
                  <IconComponent className="w-4 h-4 text-accent flex-shrink-0" />
                  <span className="text-sm font-semibold text-foreground">{item.text}</span>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <SectionHeader
            eyebrow="For Corporate Buyers"
            headline="Why organisations choose Sammo"
            subheadline="A platform that turns everyday household challenges into daily engagement and measurable business value."
            align="center"
          />
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-12"
          >
            {BUSINESS_VALUE.map((item, i) => {
              const Icon = item.icon
              return (
                <motion.div key={i} variants={staggerItem}>
                  <Card className="p-8 h-full hover:shadow-lg transition-all group rounded-2xl">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent to-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </Card>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <SectionHeader
            eyebrow="The Platform"
            headline="22+ assistants for everyday life"
            subheadline="From school homework to money management, health questions to legal guidance — practical help for the whole family."
            align="center"
            ctaText="View All Assistants"
            ctaHref={ROUTE_PATHS.ASSISTANTS}
          />
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mt-12"
          >
            {featuredAssistants.map((assistant) => (
              <motion.div key={assistant.id} variants={staggerItem}>
                <AssistantCard assistant={assistant} />
              </motion.div>
            ))}
          </motion.div>
          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg">
              <Link to={ROUTE_PATHS.ASSISTANTS}>View All Assistants</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-4">
                Family Value
              </span>
              <h2 className="text-4xl font-bold mb-6">Used daily across the whole household</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                One subscription supports every member of the family — from children doing homework to parents managing budgets, grandparents seeking health guidance, and teens exploring careers.
              </p>
              <Button asChild size="lg">
                <Link to={ROUTE_PATHS.FAMILY_ACCESS}>Explore Family Access</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative flex items-center justify-center"
            >
              <div className="relative w-80 h-80">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center z-10 shadow-lg">
                  <span className="text-white font-bold text-sm">Sammo</span>
                </div>
                {FAMILY_MEMBERS.map((member, i) => {
                  const angle = (i / FAMILY_MEMBERS.length) * 2 * Math.PI - Math.PI / 2
                  const radius = 110
                  const x = Math.cos(angle) * radius + 160 - 28
                  const y = Math.sin(angle) * radius + 160 - 28
                  const lineX2 = Math.cos(angle) * (radius - 36) + 160
                  const lineY2 = Math.sin(angle) * (radius - 36) + 160
                  return (
                    <div key={member.label}>
                      <svg className="absolute inset-0 w-full h-full pointer-events-none">
                        <line
                          x1="160" y1="160"
                          x2={lineX2} y2={lineY2}
                          stroke="currentColor"
                          strokeWidth="1.5"
                          className="text-accent/30"
                          strokeDasharray="4 4"
                        />
                      </svg>
                      <div
                        className={`absolute w-14 h-14 rounded-full ${member.color} flex items-center justify-center shadow-md`}
                        style={{ left: x, top: y }}
                      >
                        <div className="text-center">
                          <Users className="w-4 h-4 text-white mx-auto" />
                          <span className="text-white text-[9px] font-semibold leading-tight block">{member.label}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <SectionHeader
            eyebrow="Industries"
            headline="Trusted across sectors"
            subheadline="Sammo is designed to plug into corporate benefit structures, loyalty programmes, and customer platforms across key African industries."
            align="center"
          />
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto mt-12"
          >
            {INDUSTRIES_LIST.map((industry) => {
              const LucideMap = LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>
              const IconComponent = LucideMap[industry.icon]
              if (!IconComponent) return null
              return (
                <motion.div key={industry.id} variants={staggerItem}>
                  <Card className="p-6 text-center hover:shadow-md transition-all group rounded-2xl">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-accent/10 transition-colors">
                      <IconComponent className="w-6 h-6 text-primary group-hover:text-accent transition-colors" />
                    </div>
                    <p className="text-sm font-semibold">{industry.name}</p>
                  </Card>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${IMAGES.AFRICA_TECH_9})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/50 via-transparent to-primary/70" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-accent text-sm font-semibold mb-6">
              The African Reality
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
              Families face pressure across money, jobs, health and education.
            </h2>
            <p className="text-xl text-white/80 leading-relaxed">
              Sammo helps make practical, trusted support easier to access — through the channels families already use, in the languages they speak, for the challenges they actually face.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to grow customer value while helping families thrive?
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              Join the organisations using Sammo to create genuine everyday value for their customers, members and employees.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="text-base px-8">
                <Link to={ROUTE_PATHS.FOR_BUSINESS}>See Sammo for Your Business</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-base px-8">
                <Link to={ROUTE_PATHS.FAMILY_ACCESS}>Start Free Trial</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  )
}